import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";

const CONTACT_EMAIL = "hello@shemeanscontent.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", brand: "", message: "" });

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `New inquiry from ${form.name || "your website"}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nBrand: ${form.brand}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-charcoal text-pearl overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-lilac/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-blush/20 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div
            className="eyebrow !text-pearl/60 mb-5"
            data-testid="contact-eyebrow"
          >
            — Let&apos;s Build Something
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="contact-heading"
          >
            Tell us about your{" "}
            <span className="italic text-lilac">brand</span>.
          </h2>
          <p
            className="mt-6 text-pearl/70 text-base md:text-lg leading-relaxed max-w-md"
            data-testid="contact-subtext"
          >
            We work with a select few brands every quarter. Drop us a note —
            we&apos;ll get back within 48 hours with a clear next step.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-4 group"
              data-testid="contact-email-link"
            >
              <span className="w-11 h-11 rounded-full border border-pearl/25 flex items-center justify-center group-hover:bg-lilac group-hover:border-lilac transition-colors">
                <Mail size={16} />
              </span>
              <div>
                <div className="text-[0.68rem] uppercase tracking-[0.24em] text-pearl/50">
                  Email
                </div>
                <div className="text-base">{CONTACT_EMAIL}</div>
              </div>
            </a>

            <div
              className="flex items-center gap-4"
              data-testid="contact-phone"
            >
              <span className="w-11 h-11 rounded-full border border-pearl/25 flex items-center justify-center">
                <Phone size={16} />
              </span>
              <div>
                <div className="text-[0.68rem] uppercase tracking-[0.24em] text-pearl/50">
                  Phone
                </div>
                <div className="text-base">+1 (818) 631-7384</div>
              </div>
            </div>

            <div
              className="flex items-center gap-4"
              data-testid="contact-location"
            >
              <span className="w-11 h-11 rounded-full border border-pearl/25 flex items-center justify-center">
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-[0.68rem] uppercase tracking-[0.24em] text-pearl/50">
                  Studio
                </div>
                <div className="text-base">LA / OC Area, California - USA</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="lg:col-span-7 bg-pearl text-charcoal rounded-3xl p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[0.68rem] uppercase tracking-[0.24em] text-charcoal/55">
                Your Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Jane Doe"
                className="input-field"
                data-testid="contact-form-name"
                required
              />
            </div>
            <div>
              <label className="text-[0.68rem] uppercase tracking-[0.24em] text-charcoal/55">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="hello@brand.com"
                className="input-field"
                data-testid="contact-form-email"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[0.68rem] uppercase tracking-[0.24em] text-charcoal/55">
                Brand / Company
              </label>
              <input
                type="text"
                name="brand"
                value={form.brand}
                onChange={handleChange}
                placeholder="The brand you represent"
                className="input-field"
                data-testid="contact-form-brand"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[0.68rem] uppercase tracking-[0.24em] text-charcoal/55">
                What can we help with?
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="A short note about your goals, timeline, and channels you're focused on..."
                className="input-field resize-none"
                data-testid="contact-form-message"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary mt-10"
            data-testid="contact-form-submit"
          >
            Send Inquiry <ArrowRight size={16} />
          </button>

          <p className="mt-5 text-xs text-charcoal/50">
            By sending, you&apos;ll be redirected to your email client to
            confirm and send the message to our team.
          </p>
        </form>
      </div>
    </section>
  );
}
